// Jobs — Office records. The screen the rebuild shipped without.
//
//   GET    /api/jobs                 list (client_name, property_name, address,
//                                    directions_url, user_name, assignees,
//                                    subtask counts, entry_count, no_client)
//   POST   /api/jobs                 create        (JobForm)
//   PUT    /api/jobs/:id             edit          (JobForm)
//   DELETE /api/jobs/:id             delete — 409 when the job carries records
//   GET    /api/jobs/:id/entries     the clock entries on one job, with the
//                                    coordinates the crew phone recorded
//   GET    /api/admin/users          who can be assigned
//   GET    /api/clients              client picker
//   GET    /api/clients/properties/all  property picker
//   GET    /api/dispatch/now         who is on the clock RIGHT NOW, polled
//                                    every 30s, for the pill on a row — only
//                                    asked for by someone the server's own
//                                    door admits (apps/shared/liveClock.js)
//
// Deleting a job that has clock entries is refused by the database and
// answered as 409 `linked_records` with a sentence (server/utils/dbRefusal.js).
// That is not an error to shrug at: the classic screen offers "Archive
// Instead", and so does this one, because the person's intent was to get the
// job out of the way and archiving does that without destroying payroll
// history.

// ── The location the app collected and never showed (2026-09-06) ────────────
//
// Until now this panel said `Clock entries: 4` and nothing else. Every one of
// those entries carries a GPS fix the crew phone took at the clock in or out
// (apps/field/geo.js -> routes/field.js), and no screen in the product ever
// read one back. The owner's words: "admins don't see the location collected
// on the jobs." docs/APP_REBUILD.md §3.4 is the phase; this file is its client
// half, `GET /api/jobs/:id/entries` its server half.
//
// A count is not a lesser version of a list, it is a different claim. "4"
// answers "did anyone work here"; it cannot answer "was the crew at the
// property", which is the question the coordinates were being collected for.
//
// Two rules the panel is built on:
//
//   · An entry with no fix says SO, in words. Most historic rows have none —
//     they predate the geo capture entirely — and a blank cell or a dead link
//     reads as a broken screen rather than as an absent measurement.
//   · A refused request says so too. `accounting` is refused this endpoint for
//     any job it does not lead (the route header explains why that is not new),
//     and rendering that as an empty list would state "nobody clocked in here"
//     over a 403. Same reasoning as pickerNote.js and JobPanels' panelCount.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { post, put, patch, del } from '../../shared/http';
import { useCan, usePermissionsKnown, useRole } from '../../../hooks/usePermission';
import { useAuth } from '../../../App';
import { DataTable, Pill, FwButton, FwSelect, FwField, FwInput, Skeleton, EmptyState, Notice, SectionLabel, BigClock } from '../../../ui/fw';
import JobForm from '../../shared/JobForm';
import { SubtasksPanel, CommentsPanel, PhotosPanel, DependenciesPanel, ActivityPanel } from '../../shared/job/JobPanels';
import {
  STATUS_LABELS, JOB_STATUSES, statusLabel, jobBadges, UNASSIGNED, matchesWorker, crewSummary,
} from '../../shared/jobFields';
import {
  useOfficeQuery, useAction, PageHeader, Toolbar, SearchBox, Panel, Split, DetailList, DetailRow,
  LoadError, ConfirmSheet, matches, textOr, factFallback, fmtDate, jobTone, hoursOf,
  useRecordView, recordById, DetailActions, MoreFilters, countActive,
} from './OfficePage';
import { pickerNote } from '../../shared/pickerNote';
import { NO_CLIENT, clientKey, clientOptions } from '../shared/jobsByClient';
// `gpsText` and `mapHref` are the app's ONE declared reading of a GPS fix
// (apps/shared/geoFix.js), written with a Number.isFinite guard and a comment
// about why truthiness is wrong for a coordinate — a 0 latitude is a real
// place. Reports, the CSV export and the live board all go through them; a
// second coordinate formatter here is how two screens end up disagreeing about
// the same row. `isRunning` is the same idea for "has this shift ended".
import { gpsText, mapHref } from '../../shared/geoFix';
import { isRunning } from '../insight/reportsContract';
import {
  fetchLiveClock, LIVE_CLOCK_KEY, LIVE_CLOCK_ENDPOINT, mayReadLiveClock, runningByJob, runningWho, elapsedNow,
} from '../../shared/liveClock';
// parseUTCDate is the date-range filter's reading of created_at.
import { formatDateOnly, formatTimeInUserTZ, parseUTCDate } from '../../../utils/timezone';
// compactDuration is the formatter the Schedule agenda already renders
// jobs.estimated_time with (utils/agenda.js jobDuration). Seconds -> "1h 30m".
// Reused rather than reinvented so the two screens can never disagree about
// the same column, which is how #620 got as far as it did.
import { compactDuration } from '../../../utils/agenda';
import {
  JOB_MONEY_ENDPOINT, moneyById, jobStatusLabel, billingPill, payrollPill, isProcessed,
  BILLING_FILTER_VALUES, BILLING_FILTER_LABELS, matchesBilling, EMPTY_BILLING, EMPTY_PAYROLL,
} from '../shared/jobMoney';

const SEARCH_FIELDS = ['title', 'description', 'client_name', 'property_name', 'user_name'];

// The crew sentence lives in apps/shared/jobFields.js now — the FIELD job
// card shows the same thing (docs/PARITY_2026-09-08.md §8) and two copies of
// one rule is how two screens end up disagreeing about one row. Re-exported
// so this file's own call sites and OfficeJobs.test.js are unchanged.
export { crewSummary };

/** How many work-list items are still outstanding. 0 when there is no list. */
export function workLeft(job) {
  const total = (job && job.subtask_count) || 0;
  if (!total) return 0;
  return Math.max(0, total - ((job && job.subtask_completed_count) || 0));
}

/**
 * The work list, as a person scanning a list reads it.
 *
 * ── Why not "3 of 7" (2026-09-11) ──────────────────────────────────────────
 *
 * This printed `${done} of ${total}` until now. The question somebody scans a
 * job list to answer is "is there work outstanding on this job", and "3 of 7"
 * answers it only after a subtraction — on every row, in their head. "4 left"
 * is the same fact with the arithmetic already done, and "All 7 done" states
 * the finished case positively rather than making "7 of 7" be parsed.
 *
 * The progress form is not lost: the work-list PANEL heading still reads
 * "Work list (3 of 7)" (JobPanels.js panelCount), and it is sitting directly
 * on top of the items, which is the one place where the ratio is what you
 * want — you are looking at the list it describes.
 *
 * An empty string when there is no list at all, and the CALLER supplies the
 * placeholder, because the two presentations want different ones and
 * RecordList already knows the difference: a table cell needs to hold its
 * place in the grid, and a card's fact slot must yield to a column that says
 * something (RecordList.js isEmptyFact treats an em dash as blank on purpose).
 */
export function workListSummary(job) {
  const total = (job && job.subtask_count) || 0;
  if (!total) return '';
  const left = workLeft(job);
  return left === 0 ? `All ${total} done` : `${left} left`;
}

/**
 * Blocked is NOT a status — it is a non-null `blocked_reason` alongside one
 * (server/utils/scheduling.js isBlocked). A job can be active AND blocked, so
 * it needs its own marker rather than a sixth entry in the status list.
 */
export function isBlocked(job) {
  return !!(job && job.blocked_reason);
}

// ── The two filters this screen lost ───────────────────────────────
//
// docs/PARITY_2026-09-08.md §13: the classic Jobs list filtered by date range
// and by tracking status ("Filter by date range", "Filter by tracking status")
// and the rebuilt screen kept only search, status and crew.
//
// Both are answered client-side, from fields GET /api/jobs already sends.
// Neither is a query parameter the server offers: `/jobs` reads `status` alone
// (server/routes/jobs.js:196) and `/jobs/all` reads `user_id` and `status`
// (:257). There is nothing to send for either of these, so sending one would
// be a control that lies about where it is answered.

/** What the tracking filter may hold, and what each option says. */
export const TRACKING_VALUES = ['', 'tracked', 'untracked'];
export const TRACKING_LABELS = {
  '': 'Any tracking',
  tracked: 'Time tracked',
  untracked: 'No time yet',
};

/**
 * Has anybody put time on this job?
 *
 * `entry_count` is a COUNT(*) over time_entries the list query computes for
 * every row (server/routes/jobs.js:203). The classic screen answered this from
 * the LIVE timer list instead, so its "Not Tracked" option was true of a job
 * with two hundred recorded hours on it as long as nobody was clocked in right
 * now — and "who is on the clock" is what the Dispatch Now board is for. This
 * answers the durable question, which is the one the office has no other way
 * to ask.
 *
 * A row with NO count matches neither bucket. The list endpoint's emergency
 * fallback (`SELECT * FROM jobs`, :245) omits the column, and a missing count
 * is not a zero: printing "No time yet" over a job full of payroll hours is a
 * plausible-looking wrong answer, which CLAUDE.md calls the worst outcome
 * available here. A degraded list therefore shows the empty state and its way
 * back rather than a confident lie.
 */
export function matchesTracking(job, tracking) {
  if (!tracking) return true;
  const n = job ? job.entry_count : undefined;
  if (n === null || n === undefined) return false;
  return tracking === 'tracked' ? Number(n) > 0 : Number(n) === 0;
}

/** What the date filter may hold. */
export const CREATED_VALUES = ['', 'today', 'week', 'month'];
export const CREATED_LABELS = {
  '': 'Any time',
  today: 'Today',
  week: 'Last 7 days',
  month: 'Last 30 days',
};

/**
 * The earliest `created_at` a range admits, or null for "any time".
 *
 * Local midnight, so "Today" means the day the person is having rather than a
 * rolling 24 hours. The classic computed its "Last 30 Days" with
 * `setMonth(-1)`, which is a calendar month and is 28, 30 or 31 days — the
 * label was wrong for eleven months of the year. This one counts the days the
 * label names.
 */
export function createdFloor(range, now = new Date()) {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === 'today') return midnight;
  if (range === 'week' || range === 'month') {
    const d = new Date(midnight);
    d.setDate(d.getDate() - (range === 'week' ? 7 : 30));
    return d;
  }
  return null;
}

/**
 * Was this job created inside the range?
 *
 * Through parseUTCDate, because `jobs.created_at` is a bare SQLite
 * CURRENT_TIMESTAMP with no zone marker and `new Date()` reads that as LOCAL —
 * four hours late in Eastern, and on the wrong calendar DAY for anything
 * created after 8pm. The classic carries the same note; a job entered at 9pm
 * yesterday used to come back from the "Today" filter.
 *
 * A missing or unparseable date falls out of every range, and never out of
 * "any time".
 */
export function matchesCreated(job, range, now) {
  const floor = createdFloor(range, now);
  if (!floor) return true;
  const at = parseUTCDate(job && job.created_at);
  return !!at && !Number.isNaN(at.getTime()) && at >= floor;
}

/**
 * The status filter's choices beyond the five statuses themselves.
 *
 * '' (the default) is every job, archived included — and stays so on
 * purpose. Approving a timesheet archives its finished jobs, and those rows
 * are the office's billing to-do list: "Processed · Not billed" in the list
 * and "Finished, not billed" in the Billing filter are archived jobs. A first
 * cut hid archived jobs by default, which emptied that list without a word
 * (review of #738, measured by ui-check job-money). Hiding them is a choice:
 * "All but archived".
 *
 * 'open' and 'blocked' are the two lists Overview's tiles count (routes/
 * office.js): open = to do, active or paused; blocked = a blocked_reason on a
 * job that is not done. Those tiles used to open /board, which shows today
 * and tomorrow only, so most of the jobs they counted were not on it.
 */
export const STATUS_FILTERS = [
  { value: '', label: 'Any status' },
  { value: 'open', label: 'Open (to do, active, paused)' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'not_archived', label: 'All but archived' },
  ...JOB_STATUSES.map((st) => ({ value: st, label: STATUS_LABELS[st] })),
];
const OPEN_STATUSES = ['todo', 'active', 'paused', 'in_progress'];
const DONE_STATUSES = ['archived', 'cancelled', 'completed'];

export function matchesStatus(job, status) {
  const st = job && job.status;
  if (!status) return true;
  if (status === 'not_archived') return st !== 'archived';
  if (status === 'open') return OPEN_STATUSES.includes(st);
  if (status === 'blocked') return isBlocked(job) && !DONE_STATUSES.includes(st);
  return st === status;
}

/**
 * Rows the filters let through. Exported so the filter logic is testable.
 *
 * `client` is a client id, or 'none' for jobs with no client — the same key
 * Bill finished jobs and the invoice editor group by (shared/jobsByClient.js).
 * The owner, 2026-09-26: "Where do I filter jobs by client!?"
 */
/**
 * "No client", as the server says it: the `no_client` flag every row carries
 * (routes/jobs.js flagNoClient — no client AND no property, so the job can
 * never reach an invoice). Overview's tile counts that, and the list it opens
 * must be the same question (review of #738). A row without the flag falls
 * back to the client id.
 */
export function isNoClientJob(j) {
  return j && typeof j.no_client === 'boolean' ? j.no_client : clientKey(j) === 'none';
}

export function filterJobs(jobs, { q, status, client, assignee, tracking, created, now } = {}) {
  return (jobs || []).filter((j) => {
    if (!matchesStatus(j, status)) return false;
    if (client === 'none' ? !isNoClientJob(j) : (client && clientKey(j) !== client)) return false;
    if (!matchesWorker(j, assignee)) return false;
    if (!matchesTracking(j, tracking)) return false;
    if (!matchesCreated(j, created, now)) return false;
    return matches(j, SEARCH_FIELDS, q);
  });
}

/**
 * The maps URL for one clock entry, or '' when the entry carries no fix.
 *
 * MOVED to apps/shared/geoFix.js on 2026-09-09 and re-exported here, so this
 * screen's callers and OfficeJobs.test.js are untouched. This comment used to
 * say the URL shape was copied from apps/dispatch/screens/Now.js "character
 * for character, because shipping a second spelling of the same link would
 * mean two behaviours to keep working" — there is now one spelling, in one
 * file, and Now.js imports it too.
 */
export { mapHref };

/**
 * When a shift ran, as one line. The date is printed once and the two clock
 * times share it, because on a 320px screen two full timestamps wrap to four
 * lines and say the same thing.
 *
 * A running entry says "still on the clock" rather than showing an end — the
 * server sends `end_time: null` for it and inventing "now" would be a claim
 * about a shift that has not finished.
 */
export function entryWhen(entry) {
  if (!entry || !entry.start_time) return 'Time not recorded';
  const day = formatDateOnly(entry.start_time);
  const from = formatTimeInUserTZ(entry.start_time);
  if (isRunning(entry)) return `${day} · from ${from} · still on the clock`;
  const to = formatTimeInUserTZ(entry.end_time);
  return `${day} · ${from} – ${to}`;
}

/**
 * The clock entries on one job: who, when, how long, and where.
 *
 * Rendered as a list of stacked blocks rather than a table on purpose
 * (docs/APP_REBUILD.md §2): this panel is opened on a phone, a table of six
 * columns is 600px wide inside a 360px screen, and the measured symptom the
 * whole rebuild exists to fix is exactly that sideways pan.
 */
function ClockEntries({ jobId }) {
  const q = useOfficeQuery(['job', String(jobId), 'entries'], `/jobs/${jobId}/entries`);
  const rows = q.data || [];
  // `q.data || []` cannot tell "no entries" from "refused", so the count is
  // only shown when the request actually answered — a heading reading
  // "Clock entries (0)" over a 403 is arithmetic, and nobody questions
  // arithmetic. Same lesson as JobPanels' panelCount.
  const count = q.isLoading || q.error ? undefined : rows.length;

  return (
    <section className="or-entries">
      <header className="or-entries__head">
        <SectionLabel as="h3">Clock entries{count === undefined ? '' : ` (${count})`}</SectionLabel>
      </header>

      <LoadError error={q.error} fallback="Could not load the clock entries for this job." />

      {q.isLoading ? <Skeleton lines={3} /> : null}

      {!q.isLoading && !q.error && !rows.length ? (
        <p className="or-entries__none">Nobody has clocked in on this job yet.</p>
      ) : null}

      {rows.length ? (
        <ul className="or-entries__list">
          {rows.map((e) => {
            const href = mapHref(e);
            return (
              <li key={e.id} className="or-entries__item">
                <div className="or-entries__who">
                  <span className="or-entries__name">{e.user_name || 'Unknown worker'}</span>
                  {isRunning(e)
                    ? <Pill tone="now">On the clock</Pill>
                    : <span className="or-entries__hours">{hoursOf(e.duration)} h</span>}
                </div>
                <div className="or-entries__when">{entryWhen(e)}</div>
                {e.start_note ? <div className="or-entries__note" data-entry-note="start">Started: {e.start_note}</div> : null}
                {e.stop_note ? <div className="or-entries__note" data-entry-note="stop">Stopped: {e.stop_note}</div> : null}
                <div className="or-entries__where">
                  {href ? (
                    <>
                      {/*
                        The coordinates sit BESIDE the link rather than inside
                        it, and that is a measurement rather than a preference:
                        with them in the label the control rendered 246px wide
                        inside a 320px screen — 77% of the viewport, which
                        docs/APP_REBUILD.md §5 invariant 3 refuses. They are
                        data about the entry; only "Open in maps" is a control.
                      */}
                      <a
                        className="or-entries__map"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in maps
                      </a>
                      <span className="or-entries__coords">{gpsText(e)}</span>
                    </>
                  ) : (
                    // Said in words, not left blank. Most entries older than
                    // the geo capture have no fix, and an empty space where a
                    // link lives on the row above reads as a bug in the screen.
                    <span className="or-entries__nowhere">No location recorded</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Is the detail column BESIDE the list, or stacked underneath it?
 *
 * ── Why this is measured and not a breakpoint (2026-09-11) ─────────────────
 *
 * `.or-split` is `flex-wrap: wrap` with `main { flex: 1 1 480px }` and
 * `aside { flex: 0 1 360px }`, so the aside drops to its own row whenever the
 * two no longer fit — a width that depends on the shell's padding, not on
 * `usePhoneLayout`'s 768. Asking the two boxes is exact at every width and
 * cannot drift from the stylesheet the way a second copy of the number would.
 *
 * "Beside" means they share vertical space: the list's box has not finished
 * before the detail's begins. Stacked, `main.bottom <= aside.top`.
 */
export function detailIsBeside(mainRect, asideRect) {
  if (!mainRect || !asideRect) return false;
  return mainRect.bottom > asideRect.top + 1;
}

/**
 * The live "somebody is on this right now" marker for one row.
 *
 * docs/PARITY_2026-09-08.md §17: the classic jobs list put one of these on
 * every row (`ActiveTimerBadge.js`, "Renders a pulsing pill showing the
 * elapsed time on an active timer … manager view sees worker name"), and the
 * rebuild shipped without it. The information moved to a different SCREEN
 * (/now), which is not the same capability: a dispatcher scanning the jobs
 * list could no longer tell a job with work happening on it from one nobody
 * has touched.
 *
 * It renders inside the TITLE cell rather than as a column of its own, and
 * that is a measured decision, not a tidy one: RecordList shows a card on a
 * phone with at most three facts (RECORD_FACT_LIMIT), and this list already
 * has six columns after the title — an eighth would be visible on a desktop
 * table and invisible on every phone. The title is the one cell both
 * presentations always draw.
 */
function RunningPill({ entries, receivedAt, now, showWho }) {
  if (!entries || !entries.length) return null;
  const seconds = elapsedNow(entries[0].elapsed_seconds, receivedAt, now);
  const who = showWho ? runningWho(entries) : '';
  return (
    <>
      {/* `data-live` so a browser check can count the LIVE pills specifically:
          an active job's status pill is also tone "now", and a check that
          counts by tone cannot tell "one job is running" from "every job is
          active". */}
      <Pill tone="now" data-live="on-clock">On the clock</Pill>
      {/*
        The elapsed figure and the name sit BESIDE the pill, not inside its
        label — the same measurement the clock-entries panel below records:
        a pill carrying "ON THE CLOCK · MARCUS REYES · 1:02" is a single
        unbreakable-looking run of text in a table cell 100px wide. The pill
        is the state; these are the data.
      */}
      {/*
        A BigClock, not a formatted string: the owner asked for "seconds, so
        we can see timer is actually running" (2026-09-10), and this row has
        re-rendered every second since the pill shipped while drawing H:MM
        into it — so it changed once a MINUTE and looked frozen in between.
        `seconds` and not `startedAt`, so the figure stays anchored on the
        server's own elapsed count via `elapsedNow`; `sm`, so it stays the
        19px the cell already printed rather than growing the row.
      */}
      <BigClock className="or-job__running" size="sm" seconds={seconds} />
      {who ? <span className="or-job__running-who">{who}</span> : null}
    </>
  );
}

/**
 * The columns, given what is running right now.
 *
 * A function rather than a constant because the running set changes every
 * thirty seconds and the elapsed clock every second; a module-level array
 * cannot see either.
 */
export function jobColumns({ running = new Map(), receivedAt, now = Date.now(), showWho = false, money = new Map(), billingKnown = false } = {}) {
  return [
  {
    key: 'title',
    label: 'Job',
    render: (j) => (
      <span className="or-job__title">
        {j.title}
        <RunningPill
          entries={running.get(String(j.id))}
          receivedAt={receivedAt}
          now={now}
          showWho={showWho}
        />
        {/*
          ── A job with work still on its list says so (2026-09-11) ──────────

          Measured at 320px before this: the `subtasks` column is SEVENTH in
          the pool, RECORD_FACT_LIMIT is 3, and planCard fills those three
          with the first columns that are not blank — so on any job with a
          client and a property the card read SCHEDULED · CREW · CLIENT and
          the work list reached no phone card in the app at all. A job with
          five unticked items was indistinguishable from one with no list.

          Here rather than in a fact slot, for the reason this file already
          gives about the status badge and the running pill: it is a STATE,
          not a fact, and the title is the one cell both presentations always
          draw. Spending one of three fact slots on it would have cost the
          card its client.

          Rendered only while items REMAIN. A pill on every finished job and
          on every job with no list is noise on most rows, and "statuses
          indistinguishable" is a complaint this app has already had. Tone
          `neutral`, not `warn`: outstanding work is the ordinary state of an
          active job, and colouring it amber would make a normal board read as
          a list of problems. `data-worklist` so a browser check can count
          these specifically — an active job's status pill is also a pill, and
          counting by tone cannot tell the two apart.
        */}
        {workLeft(j) > 0
          ? <Pill tone="neutral" data-worklist="left">{workListSummary(j)}</Pill>
          : null}
        {jobBadges(j).filter((b) => b.key !== 'status').map((b) => <Pill key={b.key} tone={b.tone}>{b.label}</Pill>)}
      </span>
    ),
  },
  // ── The order below decides what a PHONE shows ───────────────────────────
  //
  // RecordList draws a card at most RECORD_FACT_LIMIT (3) facts deep and takes
  // them in this order, so the first three non-title/subtitle/badge columns
  // are the whole card. Measured at 390px on 2026-09-10, when Client,
  // Property and Crew held those slots, every card in Office Jobs read:
  //
  //   Hedge shaping, front drive | TO DO | 18 Ridge Rd, Darien CT
  //     CLIENT Hartwell Estate   PROPERTY —   CREW System Administrator
  //
  // — no date anywhere. An admin scanning this list on a phone could not tell
  // a job scheduled today from one scheduled in March, while PROPERTY, which
  // is optional and renders "—" on most rows, spent one of the three saying
  // nothing. A job list is a diary: WHEN, then WHO, then FOR WHOM.
  //
  // Nothing is removed — the wide table still carries all eight, in this
  // order. OfficeJobs.test.js pins both halves.
  { key: 'scheduled_date', label: 'Scheduled', render: (j) => (j.scheduled_date ? fmtDate(j.scheduled_date) : 'Not scheduled') },
  { key: 'crew', label: 'Crew', render: crewSummary },
  { key: 'client_name', label: 'Client', render: (j) => textOr(j.client_name) },
  // Two columns, because they answer different questions: "which of this
  // client's properties" and "where do I drive". On a phone the card shows the
  // address as the subtitle and keeps the property as a fact — role: 'subtitle'
  // is what RecordList reads for that.
  { key: 'property_name', label: 'Property', render: (j) => textOr(j.property_name) },
  // The card's SUBTITLE, so it is the one line under the job title and it may
  // never be a dash. Measured at 320px on 2026-09-11: a job with no property
  // (client set, nothing else) rendered its subtitle as "—" — the most
  // prominent line on the card after the title, saying nothing. `jobs` has no
  // address column of its own; `address` is composed server-side from the
  // PROPERTY (server/utils/jobAddress.js), so a job with no property has none
  // by construction and that is the common case for a one-off callout.
  //
  // Property name is the fallback rather than a second dash because it is the
  // next thing that locates the work, and it is NOT otherwise on the card:
  // facts are capped at three and `property_name` sits fourth, so it is
  // table-only. Falling back to it duplicates nothing.
  {
    key: 'address',
    label: 'Address',
    role: 'subtitle',
    render: (j) => factFallback([j.address, j.property_name], 'No address on file'),
  },
  // The em dash is deliberate and is NOT the fact-slot defect it looks like.
  // RecordList.isEmptyFact reads '—' as blank, so on a card this column YIELDS
  // its slot to one that says something; in the wide table the cell holds its
  // place in the grid, which an empty box would not. Words here ("No work
  // list") would make a card spend one of three slots saying nothing.
  // `align` dropped with the ratio: "4 left" is a sentence, not a figure, and
  // `align: 'right'` also brings `fw-num` tabular alignment.
  { key: 'subtasks', label: 'Work list', render: (j) => workListSummary(j) || '—' },
  // The work status, and beside it where the job stands on BILLING — the
  // question the office opens this list to answer once work is finished
  // (office/shared/jobMoney.js). Both in the one badge cell, so the phone
  // card spends no fact slot on it and the table gains no ninth column.
  {
    key: 'status',
    label: 'Status',
    role: 'badge',
    render: (j) => {
      // Only when the billing half actually ARRIVED. Without it — the
      // request failed, or this viewer may not see billing — a finished job
      // would say "Not billed", which is a claim about money nobody measured
      // (found by unmounting the endpoint under the browser check).
      const bill = billingKnown ? billingPill(j, (money.get(String(j.id)) || {}).billing) : null;
      return (
        <span className="or-job__states">
          <Pill tone={jobTone(j.status)} data-processed={isProcessed(j) ? '' : undefined}>{jobStatusLabel(j, statusLabel)}</Pill>
          {bill ? <Pill tone={bill.tone} data-billing={((money.get(String(j.id)) || {}).billing || {}).state || 'unbilled'}>{bill.label}</Pill> : null}
        </span>
      );
    },
  },
  ];
}

function JobDetail({ job, jobs, role, user, currentUserId, canUpdate, canDelete, full, onToggleFull, onEdit, onClose, onChanged, money }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [blockSheet, setBlockSheet] = useState(false);
  const [reason, setReason] = useState('');
  const { busy, error, run, setError } = useAction();
  const panelRef = useRef(null);

  // ── Tapping a job on a phone has to SHOW you the job (2026-09-11) ─────────
  //
  // The owner: "We need to improve the other pages..specially jobs, don't see
  // checklists". The work list has been in the DOM the whole time. Measured in
  // a real browser at 320x568 against a 14-job list — which is a small board
  // for this company — after tapping a card:
  //
  //   the detail column's own top       652px BELOW the fold (1.15 screenfuls)
  //   the WORK LIST heading            1771px below it       (3.12 screenfuls)
  //   the whole page                   6489px tall
  //
  // and tapping the FIRST card in that list puts the detail ~3200px down,
  // because `.or-split` wraps on a phone and the aside is rendered after the
  // ENTIRE list. So the visible result of tapping a job is nothing moving.
  // Nobody scrolls three screens on the chance that something appeared.
  //
  // At 1440 the aside is sticky beside the list and its top sits 20px below
  // the fold, so there is nothing to scroll to and doing it anyway would yank
  // a desktop page for no reason — hence the measurement rather than a
  // blanket scroll. Same guard for `?view=full`, where the record IS the page
  // and `.or-split__main` is not rendered at all (OfficePage.js Split).
  //
  // Keyed on the job id, so opening a SECOND job from a long list brings that
  // one into view too; `scrollIntoView` is feature-detected because jsdom does
  // not implement it and an unguarded call would throw in every screen suite.
  useEffect(() => {
    const el = panelRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const split = typeof el.closest === 'function' ? el.closest('.or-split') : null;
    const main = split ? split.querySelector('.or-split__main') : null;
    if (!main) return;
    if (detailIsBeside(main.getBoundingClientRect(), el.getBoundingClientRect())) return;
    el.scrollIntoView({ block: 'start' });
  }, [job.id]);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['jobs'] });
  };

  const setStatus = (status) => run(async () => {
    await put(`/jobs/${job.id}`, { status });
    await refresh();
  }, 'Could not change the status.');

  /**
   * Bring an archived job back (issue #10).
   *
   * The status dropdown beside this could already move a job out of
   * 'archived' — and that is precisely why this button exists rather than a
   * note telling people to use the dropdown. A PUT leaves
   * `archived_timesheet_id` set, so the job goes on pointing at the sheet
   * that archived it; `hoursModel.validateLines` exempts an archived job from
   * the "archived jobs cannot be added to timesheets" refusal when the sheet
   * being edited is that one, and a stale pointer keeps a job matching a rule
   * about a state it is no longer in.
   *
   * POST /jobs/:id/unarchive clears the link, logs the act, and refuses a
   * target of 'archived'. It is also findable: timesheets.js archives every
   * COMPLETED job on a sheet when that sheet is approved (it archived EVERY
   * job until #691, which is what made this matter), so a fortnightly job
   * vanishes after its first paid visit, and "look in a status dropdown" is
   * not an answer somebody arrives at.
   */
  const unarchive = () => run(async () => {
    await post(`/jobs/${job.id}/unarchive`, {});
    await refresh();
  }, 'Could not unarchive that job.');

  // A non-empty reason blocks and stamps blocked_at; null clears it. The
  // reason is the point — "blocked" with no cause tells a dispatcher nothing.
  const block = async () => {
    // An empty reason normalises to null server-side, which UNBLOCKS. A button
    // labelled "Mark blocked" must never do the opposite of what it says.
    if (!reason.trim()) { setError('Say why it is blocked.'); return; }
    const ok = await run(async () => {
      await patch(`/jobs/${job.id}/schedule`, { blocked_reason: reason.trim() });
      await refresh();
    }, 'Could not block the job.');
    if (ok) { setBlockSheet(false); setReason(''); }
  };

  // POST /jobs/:id/claim refuses with 400 when the job already has a lead, so
  // it is only offered on an unassigned one. The server sets the caller as the
  // lead — the pointer payroll reads — and writes a 'claimed' activity line.
  const claim = () => run(async () => {
    await post(`/jobs/${job.id}/claim`, {});
    await refresh();
  }, 'Could not take the job.');

  const unblock = () => run(async () => {
    await patch(`/jobs/${job.id}/schedule`, { blocked_reason: null });
    await refresh();
  }, 'Could not unblock the job.');

  const archive = async () => {
    const ok = await run(async () => {
      await put(`/jobs/${job.id}`, { status: 'archived' });
      await refresh();
    }, 'Could not archive the job.');
    if (ok) { setRefusal(null); setConfirm(false); onChanged(); }
  };

  const remove = async () => {
    setRefusal(null);
    try {
      await del(`/jobs/${job.id}`);
      await refresh();
      setConfirm(false);
      onChanged();
    } catch (err) {
      // 409 linked_records is the database protecting recorded work, not a
      // failure to handle silently — offer the thing the person actually meant.
      if (err && err.status === 409) setRefusal(err.body || { error: err.message });
      else setRefusal({ error: (err && err.message) || 'Could not delete the job.' });
    }
  };

  return (
    <div className="or-job" ref={panelRef}>
    <Panel
      title={job.title}
      actions={<DetailActions full={full} onToggleFull={onToggleFull} onClose={onClose} />}
    >
      <LoadError error={error ? { message: error } : null} />
      {job.no_client ? (
        <Notice tone="warn" title="No client on this job">
          Work here cannot be invoiced until a client is set.
        </Notice>
      ) : null}
      {isBlocked(job) ? (
        <Notice tone="stop" title="Blocked">{job.blocked_reason}</Notice>
      ) : null}
      <DetailList>
        <DetailRow label="Status"><Pill tone={jobTone(job.status)}>{jobStatusLabel(job, statusLabel)}</Pill></DetailRow>
        {/* Payroll and billing, derived from the timesheets, paystubs and
            invoices themselves (office/shared/jobMoney.js) — only for someone
            allowed to see them, which is what `money` being set means. */}
        {money && money.payroll ? (
          <DetailRow label="Payroll">
            <span data-payroll={money.payroll.state}>
              {(payrollPill(money.payroll)
                // Put away by an approval whose line for it carried no hours:
                // the list says "Processed", so this must not say "no hours"
                // as though payroll never touched it (review, #705).
                || (money.payroll.processed_by_timesheet != null
                  ? { label: 'Processed by payroll · no hours on its sheet' }
                  : { label: 'No hours on a timesheet' })).label}
              {money.payroll.state === 'pending' && Number(money.payroll.hours_approved) > 0
                ? ` · ${Math.round(Number(money.payroll.hours_approved) * 100) / 100} h approved` : ''}
            </span>
          </DetailRow>
        ) : null}
        {money && money.billing ? (
          <DetailRow label="Billing">
            <span data-billing={money.billing.state}>
              {(() => { const p = billingPill(job, money.billing) || { label: 'Not billed yet' }; return p.long || p.label; })()}
              {money.billing.latest && money.billing.latest.status && money.billing.state === 'invoiced'
                && money.billing.latest.status !== 'cancelled'
                ? ` (${money.billing.latest.status})` : ''}
              {/* Hand-archived and unbilled: the Billing filter counts it as
                  finished, but "Invoice from jobs" lists it only with Include
                  archived ticked — say so, so the two lists disagreeing does
                  not read as a bug (review, #705). */}
              {job.status === 'archived' && !isProcessed(job) && money.billing.state === 'unbilled'
                ? ' · archived by hand, so Invoice from jobs lists it under Include archived' : ''}
              {Number(money.billing.invoice_count) > 1 ? ` · on ${money.billing.invoice_count} invoices` : ''}
            </span>
          </DetailRow>
        ) : null}
        <DetailRow label="Client">{textOr(job.client_name)}</DetailRow>
        <DetailRow label="Property">{textOr(job.property_name)}</DetailRow>
        <DetailRow label="Address">
          {job.address
            ? (job.directions_url
                ? <a href={job.directions_url} target="_blank" rel="noopener noreferrer">{job.address}</a>
                : job.address)
            : 'No address on file'}
        </DetailRow>
        <DetailRow label="Lead">{textOr(job.user_name) === '—' ? 'Unassigned' : job.user_name}</DetailRow>
        <DetailRow label="Crew">{crewSummary(job)}</DetailRow>
        <DetailRow label="Scheduled">{job.scheduled_date ? fmtDate(job.scheduled_date) : 'Not scheduled'}</DetailRow>
        {job.scheduled_end_date ? <DetailRow label="Until">{fmtDate(job.scheduled_end_date)}</DetailRow> : null}
        {job.recurrence ? <DetailRow label="Repeats">{job.recurrence}</DetailRow> : null}
        {isBlocked(job) ? <DetailRow label="Blocked because">{job.blocked_reason}</DetailRow> : null}
        {/* The same sentence the list prints, so a person who filtered on it
            and then opened the record reads the same words twice rather than
            two spellings of one fact. "None" here and an em dash in the list
            because this row is a labelled line, not a card's fact slot. */}
        <DetailRow label="Work list">{workListSummary(job) || 'None'}</DetailRow>
        {/* Seconds, formatted — not "{estimated_time} min", which read the
            column in the wrong unit and printed "5400 min" for 90 minutes. */}
        {compactDuration(job.estimated_time) ? <DetailRow label="Estimated">{compactDuration(job.estimated_time)}</DetailRow> : null}
        {/*
          ── The ACTUAL, next to the estimate (2026-09-11) ───────────────────

          This panel showed the estimate and never what the job actually took,
          so the one question an estimate exists to answer — did it hold? —
          could not be asked anywhere in Office. `jobs.total_time` has been on
          this payload all along (GET /api/jobs is `SELECT j.*`) and no screen
          read it back.

          It is the SERVER's own figure: routes/timeEntries.js maintains it as
          `SUM(duration) FROM time_entries WHERE job_id = ?` on every clock
          out. Nothing is computed here and NO VARIANCE IS SHOWN — the two
          durations sit side by side and a person compares them. Subtracting
          one from the other would be new arithmetic on hours that become
          payroll, which is not this lane's to invent (CLAUDE.md rule 4).

          Through `compactDuration`, the same formatter as the estimate above,
          so the two lines are directly comparable rather than one being "1h
          30m" and the other "1.50".

          Shown only once there IS time on the job: "0m" against an estimate
          reads as a measurement, and on a job nobody has clocked into it is
          the absence of one. `entry_count` is what distinguishes those, and
          the clock-entries panel below says so in words either way.
        */}
        {job.entry_count > 0 && compactDuration(job.total_time)
          ? <DetailRow label="Actual">{compactDuration(job.total_time)}</DetailRow>
          : null}
        <DetailRow label="Created">{fmtDate(job.created_at)}</DetailRow>
        {job.description ? <DetailRow label="Description">{job.description}</DetailRow> : null}
      </DetailList>

      {canUpdate ? (
        <div className="or-actions">
          <FwButton variant="ghost" icon="edit" onClick={onEdit}>Edit</FwButton>
          <FwSelect
            aria-label="Change status"
            value={job.status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={busy}
          >
            {JOB_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </FwSelect>
          {!job.user_id ? (
            <FwButton variant="ghost" icon="users" onClick={claim} loading={busy}>Assign to me</FwButton>
          ) : null}
          {job.status === 'archived' ? (
            <FwButton variant="go" icon="refresh" onClick={unarchive} loading={busy} data-action="unarchive">
              Unarchive
            </FwButton>
          ) : null}
          {isBlocked(job)
            ? <FwButton variant="ghost" icon="play" onClick={unblock} loading={busy}>Unblock</FwButton>
            : <FwButton variant="ghost" icon="pause" onClick={() => setBlockSheet(true)}>Mark blocked</FwButton>}
          {canDelete ? <FwButton variant="stop" icon="trash" onClick={() => setConfirm(true)}>Delete</FwButton> : null}
        </div>
      ) : null}

      {/*
        ── The order of these is the answer to "what is this page FOR" ───────

        The work list was SECOND, behind the clock entries, and that is
        backwards for this screen. Somebody opening a job in the office is
        asking what is left to do; the clock log answers what it has cost, and
        the facts list directly above already carries that as one line
        ("Actual 1h 30m", added 2026-09-11) for the case where the summary is
        all you wanted. A log of shifts is evidence you go looking for; a work
        list is the job.

        Measured at 320px, a job with seven items and two clock entries: the
        clock-entries panel is 335px tall, so it stood between the action row
        and the first thing anybody came to read. Moving it below puts the
        WORK LIST heading and its first item at the top of the panel stack.

        Nothing is hidden and nothing collapses — see the PR for why that was
        considered and left out.
      */}
      <div className="or-job__panels">
        <SubtasksPanel
          jobId={job.id}
          canEdit={canUpdate}
          canDelete={canDelete}
          user={user}
          jobs={jobs}
          onMoved={refresh}
        />
        <ClockEntries jobId={job.id} />
        <PhotosPanel jobId={job.id} canEdit={canUpdate} />
        <CommentsPanel jobId={job.id} currentUserId={currentUserId} role={role} />
        <DependenciesPanel jobId={job.id} jobs={jobs} canEdit={canUpdate} />
        <ActivityPanel jobId={job.id} />
      </div>

      <ConfirmSheet
        open={blockSheet}
        title={`Block ${job.title}?`}
        confirmLabel="Mark blocked"
        tone="primary"
        busy={busy}
        onConfirm={block}
        onClose={() => { setBlockSheet(false); setReason(''); }}
      >
        <p>The crew sees this on the job, and the board shows how long it has been stuck.</p>
        <FwField label="Why is it blocked?" htmlFor="job-block-reason" required>
          <FwInput
            id="job-block-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Waiting on the mulch delivery"
            maxLength={200}
          />
        </FwField>
      </ConfirmSheet>

      <ConfirmSheet
        open={confirm}
        title={`Delete ${job.title}?`}
        confirmLabel="Delete job"
        busy={busy}
        onConfirm={remove}
        onClose={() => { setConfirm(false); setRefusal(null); }}
      >
        {refusal ? (
          <>
            <Notice tone="stop" title="This job cannot be deleted">
              {refusal.error || 'It has records against it.'}
            </Notice>
            <p>Archiving takes it off the active list and keeps every hour recorded against it.</p>
            <FwButton variant="primary" full onClick={archive} loading={busy}>Archive instead</FwButton>
          </>
        ) : (
          <>
            <p>
              {job.entry_count
                ? `This job has ${job.entry_count} clock entr${job.entry_count === 1 ? 'y' : 'ies'} against it, so the database will refuse to delete it.`
                : 'This removes the job, its work list and its comments.'}
            </p>
            <p>Archiving keeps the history and takes it off the active list.</p>
          </>
        )}
      </ConfirmSheet>
    </Panel>
    </div>
  );
}

export default function Jobs() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const role = useRole();
  // useCan takes (resource, action). These read `useCan('jobs.create')` until
  // 2026-09-03, which asks for a RESOURCE named "jobs.create" — no role has
  // one, so `perms['jobs.create']` was undefined and the hook returned false
  // for everybody except an admin, who short-circuits before the lookup.
  //
  // The effect was not subtle: a MANAGER saw no New job, no Edit, no Delete,
  // no status change and no Mark blocked on this screen — the whole action row
  // is behind canUpdate — while the server allowed all of it. Admin-only
  // testing hides this completely, which is why scripts/ui-check.js now has a
  // `manager-jobs` scenario and server/utils/__tests__/useCanArity.test.js
  // scans for the shape.
  const canCreate = useCan('jobs', 'create');
  const canUpdate = useCan('jobs', 'update');
  const canDelete = useCan('jobs', 'delete');
  // The two asks that mirror the live board's door (apps/shared/liveClock.js).
  // Both unconditional: `useCan(…) || useCan(…)` would skip the second call
  // whenever the first is true, and a hook that is sometimes called is a hook
  // order that changes between renders.
  const canApproveHours = useCan('timesheets', 'approve');
  const canLeadTeam = useCan('timesheets', 'manage_team');

  const jobsQ = useOfficeQuery(['jobs'], '/jobs');
  // Payroll and billing state per job — asked for only by someone the server
  // will answer (it gates each half on invoices.read / timesheets.approve).
  const canSeeBilling = useCan('invoices', 'read');
  const canReadAllSheets = useCan('timesheets', 'read_all');
  const canSeePayroll = canApproveHours || canReadAllSheets;
  const moneyQ = useOfficeQuery(['job-money'], JOB_MONEY_ENDPOINT, { enabled: canSeeBilling || canSeePayroll });
  const money = useMemo(() => moneyById(moneyQ.data), [moneyQ.data]);
  const billingKnown = !!(moneyQ.data && moneyQ.data.billing);
  const clientsQ = useOfficeQuery(['clients'], '/clients');
  const propsQ = useOfficeQuery(['properties'], '/clients/properties/all');
  const usersQ = useOfficeQuery(['admin-users'], '/admin/users', { enabled: role === 'admin' || role === 'manager' });
  // Who is on the clock right now, for the pill on the row.
  //
  // `enabled` mirrors the server's own door (apps/shared/liveClock.js), not a
  // role list: without it a bookkeeper — who reaches this screen and does not
  // hold timesheets.approve — would fire a request that can only ever answer
  // 403, every thirty seconds, and the console would fill with it.
  //
  // Thirty seconds, matching the live board. NOT faster: the one-app pass
  // already shipped a badge poll that walked into server.js's rate limiter
  // (CLAUDE.md), and this screen is left open all day.
  const liveQ = useOfficeQuery(LIVE_CLOCK_KEY, LIVE_CLOCK_ENDPOINT.path, {
    enabled: mayReadLiveClock({ approve: canApproveHours, manage_team: canLeadTeam }),
    refetchInterval: 30000,
    queryFn: fetchLiveClock,
  });

  // ── The filters, in the URL ────────────────────────────────────────────────
  //
  // Same split as screens/Invoices.js, Estimates.js and Payroll.js: the SELECTS
  // live in the query string, written with `{ replace: true }`; the free-text
  // search stays in state, because a half-typed word is not a place anybody
  // wants to come back to. So "active jobs, Wes, nothing tracked yet" survives
  // a reload and can be sent to somebody, exactly as the open record already
  // could (useRecordView below). A default is left OUT of the URL rather than
  // written into it, so an unfiltered screen has a clean address.
  //
  // Every param is normalised against what the control can OFFER. A <select>
  // holding a value with no matching <option> paints the FIRST one, so
  // `?status=nonsense` would show "Any status" over a list filtered to nothing:
  // a control displaying the opposite of what it is doing.
  const [params, setParams] = useSearchParams();
  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const [q, setQ] = useState('');
  const rawStatus = params.get('status') || '';
  const status = STATUS_FILTERS.some((f) => f.value === rawStatus) ? rawStatus : '';
  const rawTracking = params.get('tracked') || '';
  const tracking = TRACKING_VALUES.includes(rawTracking) ? rawTracking : '';
  const rawCreated = params.get('since') || '';
  const created = CREATED_VALUES.includes(rawCreated) ? rawCreated : '';
  const rawClient = params.get('client') || '';
  const rawBilled = params.get('billed') || '';
  const billed = BILLING_FILTER_VALUES.includes(rawBilled) ? rawBilled : '';
  // Which job is open, and whether it has the whole page, both from the URL —
  // see OfficePage.js useRecordView. Not useState: a person who has been
  // working one job for twenty minutes and reloads must still be on it.
  const view = useRecordView();
  const [sheet, setSheet] = useState(null); // { job } | null

  const jobs = useMemo(() => jobsQ.data || [], [jobsQ.data]);
  const running = useMemo(() => runningByJob(liveQ.data), [liveQ.data]);
  // The elapsed figure on a running row, ticking between answers. The interval
  // only exists while something is actually running — a timer per second on a
  // screen with nothing live is a wakeful phone for no reason, and it is what
  // `ticking` guards on the live board too.
  // `?new=1` opens the New job form — Overview's "New job" shortcut. It used
  // to land on the list, one more tap from what it named. The flag is taken
  // off the URL as it is used, so a reload or Back does not open it again.
  //
  // Not until the permissions are known: useCan answers false before /me
  // hydrates, so on a cold load (a pasted link, the app starting from the home
  // screen) the flag was spent while the answer was still "no" and the form
  // never opened (review of #738). usePermissionsKnown sits beside useCan so
  // the two answer from the same conditions.
  const wantsNew = params.get('new') === '1';
  const permsKnown = usePermissionsKnown();
  useEffect(() => {
    if (!wantsNew || !permsKnown) return;
    if (canCreate) setSheet({ job: null });
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  }, [wantsNew, permsKnown, canCreate, params, setParams]);

  const anyRunning = running.size > 0;
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [anyRunning]);
  const columns = useMemo(
    () => jobColumns({
      running,
      receivedAt: liveQ.data && liveQ.data.received_at,
      now: tick,
      // The worker's name, for whoever is allowed to see the whole company's
      // clock — which on this screen is anybody the live request answered.
      showWho: true,
      money,
      billingKnown,
    }),
    [running, liveQ.data, tick, money, billingKnown],
  );
  const selected = useMemo(() => recordById(jobs, view.openId), [jobs, view.openId]);
  // Every client with a job, A–Z, and whether any job has none. A client id
  // the list cannot offer is dropped, for the reason every param here is
  // normalised: a select holding a value with no option paints "Any client"
  // over a list filtered to nothing.
  //
  // A client that IS on the books but has no job in this list — a client
  // page's "View jobs" opened by somebody who can see only their own jobs —
  // is kept and named from /clients, so the list reads empty under that
  // client rather than silently showing everyone's (#711 review). Only an id
  // that is no client at all is dropped.
  const knownClients = clientsQ.data;
  const jobClients = useMemo(() => {
    const opts = clientOptions(jobs);
    const known = rawClient && rawClient !== 'none' && !opts.some((c) => c.id === rawClient)
      ? (knownClients || []).find((c) => String(c.id) === rawClient) : null;
    return known ? [...opts, { id: rawClient, name: known.name || `Client #${rawClient}` }] : opts;
  }, [jobs, knownClients, rawClient]);
  const anyNoClient = useMemo(() => jobs.some(isNoClientJob), [jobs]);
  // 'none' is always a real answer, even when no job lacks a client: Overview's
  // "No client 0" tile opens ?client=none, and dropping it there opened the
  // WHOLE list under a tile that counts nothing (review of #738).
  const client = rawClient === 'none' || jobClients.some((c) => c.id === rawClient) ? rawClient : '';
  const workers = useMemo(
    () => (usersQ.data || []).filter((u) => u.is_active !== 0).map((u) => ({ id: u.id, name: u.name })),
    [usersQ.data],
  );

  // A crew id the roster does not contain is dropped once the roster has
  // ANSWERED — a link carrying a worker who has since left would otherwise
  // show "Anyone" over a list narrowed to nobody. While the request is still
  // out the value is kept, because dropping it would lose a filter the person
  // asked for a tenth of a second before it could be honoured.
  const rawAssignee = params.get('crew') || '';
  const assignee = (!rawAssignee || rawAssignee === UNASSIGNED
    || !usersQ.isSuccess
    || workers.some((w) => String(w.id) === rawAssignee)) ? rawAssignee : '';

  // `billed` counts only while the Billing control is on screen: without the
  // billing state the filter is not applied (below), and a "Clear filters"
  // for a filter nobody can see is a button that explains nothing (review, #705).
  const filtersOn = !!(q || status || client || assignee || tracking || created || (billingKnown && billed));
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    ['status', 'client', 'crew', 'tracked', 'since', 'billed'].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
    setQ('');
  };

  const rows = useMemo(
    () => filterJobs(jobs, { q, status, client, assignee, tracking, created })
      .filter((j) => !billingKnown || matchesBilling(j, (money.get(String(j.id)) || {}).billing, billed)),
    [jobs, q, status, client, assignee, tracking, created, billingKnown, money, billed],
  );

  if (jobsQ.isLoading) return <Skeleton lines={8} />;
  if (jobsQ.error) return <LoadError error={jobsQ.error} fallback="Could not load jobs." />;

  return (
    <>
      <PageHeader title="Jobs" meta={`${rows.length} of ${jobs.length}`}>
        {canCreate ? (
          <FwButton variant="primary" icon="plus" onClick={() => setSheet({ job: null })}>New job</FwButton>
        ) : null}
      </PageHeader>

      {/* A search box and two filters over a list that is not on the page are
          three controls that appear to do nothing. In full screen the record
          IS the page, so the list's controls go with the list. */}
      {view.full ? null : (
      <Toolbar>
        <SearchBox value={q} onChange={setQ} placeholder="Search jobs, clients, crew" />
        <FwSelect aria-label="Filter by status" value={status} onChange={(e) => setFilter('status', e.target.value)}>
          {STATUS_FILTERS.map((f) => <option key={f.value || 'default'} value={f.value}>{f.label}</option>)}
        </FwSelect>
        {/* Out in the open beside status, not behind More filters: it is the
            one the owner went looking for and could not find (2026-09-26). */}
        {jobClients.length || anyNoClient || client === 'none' ? (
          <FwSelect aria-label="Filter by client" value={client} onChange={(e) => setFilter('client', e.target.value)}>
            <option value="">Any client</option>
            {jobClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            {anyNoClient || client === 'none' ? <option value="none">{NO_CLIENT}</option> : null}
          </FwSelect>
        ) : null}
        {/* Everything below is behind one press on a phone and inline on a
            desktop — see MoreFilters. Search and status stay out in the open
            because they are the two anybody actually reaches for, and because
            status is the question this screen is most often opened to ask. */}
        <MoreFilters active={countActive(assignee, tracking, created, billingKnown ? billed : '')}>
        {/*
          `(usersQ.data || [])` cannot tell an empty list from a refused
          request, so a crew list that never arrived leaves this holding
          Anyone / Unassigned and nothing else — which reads as "there is no
          crew" rather than as a failure, and nobody questions data. The same
          `usersQ.error` is already handed to JobForm below; only the toolbar
          did not say it (#575 §2). Same wrapper as Activity.js and Reports.js.
        */}
        <FwField label="Crew" htmlFor="or-jobs-crew" error={pickerNote(usersQ.error, 'worker')}>
          <FwSelect
            id="or-jobs-crew"
            aria-label="Filter by crew"
            value={assignee}
            onChange={(e) => setFilter('crew', e.target.value)}
          >
            <option value="">Anyone</option>
            <option value={UNASSIGNED}>Unassigned</option>
            {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </FwSelect>
        </FwField>
        {/* docs/PARITY_2026-09-08.md §13 — both of these shipped in the classic
            screen and neither survived the rebuild. */}
        <FwField label="Tracking" htmlFor="or-jobs-tracked">
          <FwSelect
            id="or-jobs-tracked"
            aria-label="Filter by tracking status"
            value={tracking}
            onChange={(e) => setFilter('tracked', e.target.value)}
          >
            {TRACKING_VALUES.map((v) => (
              <option key={v || 'any'} value={v}>{TRACKING_LABELS[v]}</option>
            ))}
          </FwSelect>
        </FwField>
        <FwField label="Created" htmlFor="or-jobs-since">
          <FwSelect
            id="or-jobs-since"
            aria-label="Filter by date range"
            value={created}
            onChange={(e) => setFilter('since', e.target.value)}
          >
            {CREATED_VALUES.map((v) => (
              <option key={v || 'any'} value={v}>{CREATED_LABELS[v]}</option>
            ))}
          </FwSelect>
        </FwField>
        {billingKnown ? (
          <FwField label="Billing" htmlFor="or-jobs-billed">
            <FwSelect
              id="or-jobs-billed"
              aria-label="Filter by billing"
              value={billed}
              onChange={(e) => setFilter('billed', e.target.value)}
            >
              {BILLING_FILTER_VALUES.map((v) => (
                <option key={v || 'any'} value={v}>{BILLING_FILTER_LABELS[v]}</option>
              ))}
            </FwSelect>
          </FwField>
        ) : null}
        </MoreFilters>
        {filtersOn ? (
          <FwButton variant="ghost" className="or-toolbar__clear" onClick={clearFilters}>
            Clear filters
          </FwButton>
        ) : null}
      </Toolbar>
      )}

      {/* The billing and payroll state failed to load. The list still works;
          it just cannot say where any job stands on money — and says so,
          rather than dropping the pills and the Billing filter without a
          word (review, #705). */}
      {moneyQ.isError ? (
        <Notice tone="warn" title="Billing and payroll not shown" data-job-money-error>
          Where each job stands on billing and payroll could not be loaded. Every job is still listed; reload to try again.
        </Notice>
      ) : null}

      <Split
        full={view.full}
        main={
          rows.length ? (
            <DataTable
              columns={columns}
              rows={rows}
              caption="Jobs"
              onRowClick={(j) => view.open(j.id)}
            />
          ) : (
            /* A filter that matches nothing has to SAY so and offer the way
               back — an empty table with no explanation is how this app got
               called unusable. The button clears every filter AND the search
               in one press, because a person who cannot see their work does
               not want to undo four controls one at a time to find out which
               one did it. */
            <EmptyState
              title={jobs.length ? 'Nothing matches these filters' : 'No jobs yet'}
              action={filtersOn ? (
                <FwButton variant="primary" onClick={clearFilters}>Clear filters</FwButton>
              ) : null}
            >
              {jobs.length
                ? `All ${jobs.length} ${jobs.length === 1 ? 'job is' : 'jobs are'} hidden by the search or the filters above.`
                : 'Create the first job to get the crew started.'}
            </EmptyState>
          )
        }
        aside={selected ? (
          <JobDetail
            job={selected}
            money={moneyQ.data ? (money.get(String(selected.id)) || {
              billing: moneyQ.data.billing ? EMPTY_BILLING : undefined,
              payroll: moneyQ.data.payroll ? EMPTY_PAYROLL : undefined,
            }) : null}
            jobs={jobs}
            role={role}
            user={user}
            currentUserId={user && user.id}
            canUpdate={canUpdate}
            canDelete={canDelete}
            full={view.full}
            onToggleFull={view.toggleFull}
            onEdit={() => setSheet({ job: selected })}
            onClose={view.close}
            onChanged={view.close}
          />
        ) : null}
      />

      <JobForm
        open={!!sheet}
        job={sheet ? sheet.job : null}
        role={role}
        clients={clientsQ.data || []}
        properties={propsQ.data || []}
        workers={workers}
        // `|| []` above cannot tell an empty list from a refused request, and an
        // empty <select> reads as data. See apps/shared/pickerNote.js.
        listErrors={{ clients: clientsQ.error, properties: propsQ.error, workers: usersQ.error }}
        onClose={() => setSheet(null)}
        onSaved={async (saved) => {
          await qc.invalidateQueries({ queryKey: ['jobs'] });
          // `view.open` keeps the current width, so saving an edit made from a
          // full-screen job does not drop the person back into the column.
          if (saved && saved.id) view.open(saved.id);
        }}
      />
    </>
  );
}
